import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import {
  BehaviorSubject,
  catchError,
  filter,
  finalize,
  map,
  Observable,
  take,
  tap,
  throwError,
} from "rxjs";
import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { environment } from "src/environments/environment";
import { LocalStorageService } from "../local-storage.service";

@Injectable({
  providedIn: "root",
})
export class AuthServiceUser {
  private accessToken: string | null = null;
  private refreshToken: string | null = null;
  private isRefreshing = false;
  private refreshTokenSubject = new BehaviorSubject<string | null>(null);
  private isAuthenticated = new BehaviorSubject<boolean>(false);
  private accessTokenSubject = new BehaviorSubject<string | null>(null);
  public currentUserLoginInfo = new BehaviorSubject(null);
  private pageAccessData = new BehaviorSubject<any>({});
  isErrorPageShown = false;
  private loginEndpointMap: Record<string, { verify: string; check: string }> = {
    admin: {
      verify: environment.authVerifyPhoneNumber,
      check: environment.authVerifyPhoneWithOTP,
    },
    eventadmin: {
      verify: environment.authVerifyPhoneEventAdmin,
      check: environment.authVerifyPhoneWithOTPEventAdmin,
    },
    templeadmin: {
      verify: environment.authVerifyPhoneTempleAdmin,
      check: environment.authVerifyPhoneWithOTPTempleAdmin,
    },
  };
  private moduleAliases: Record<string, string[]> = {
    serviceManagement: ["service", "services", "servicesManagement", "serviceMgmt"],
    serviceProviderManagement: [
      "serviceProvider",
      "serviceProviders",
      "serviceprovider",
      "serviceproviders",
      "serviceProviderMgmt",
    ],
  };

  private actionAliases: Record<string, string[]> = {
    view: ["view", "read", "list"],
    add: ["add", "create"],
    update: ["update", "edit"],
    delete: ["delete", "remove"],
    info: ["info", "details", "view"],
  };

  constructor(
    private localStorage: LocalStorageService,
    private http: HttpClient,
    private router: Router
  ) { }

  private findModulePermission(accessData: any, moduleName: string): string[] {
    if (!accessData || typeof accessData !== "object") {
      return [];
    }

    if (Array.isArray(accessData[moduleName])) {
      return accessData[moduleName];
    }

    const normalizedMap = new Map<string, string[]>();
    Object.keys(accessData).forEach((key) => {
      if (Array.isArray(accessData[key])) {
        normalizedMap.set(key.toLowerCase(), accessData[key]);
      }
    });

    const aliases = [moduleName, ...(this.moduleAliases[moduleName] || [])].map((key) => key.toLowerCase());
    for (const alias of aliases) {
      if (normalizedMap.has(alias)) {
        return normalizedMap.get(alias) || [];
      }
    }
    return [];
  }

  getModulePermission(moduleName) {
    if (this.pageAccessData.value && Object.keys(this.pageAccessData.value).length !== 0) {
      return this.findModulePermission(this.pageAccessData.value, moduleName);
    } else {
      let data = this.localStorage.getItem('pageAccessData');
      if (data) {
        this.pageAccessData.next(data);
        return this.findModulePermission(data, moduleName);
      }

      return [];
    }
  }

  private hasActionPermission(modulePermissions: string[] = [], action: string): boolean {
    const normalizedPermissions = modulePermissions.map((item) =>
      typeof item === "string" ? item.toLowerCase() : item
    );
    const actionCandidates = [action, ...(this.actionAliases[action] || [])]
      .map((item) => item.toLowerCase());
    return actionCandidates.some((candidate) => normalizedPermissions.includes(candidate));
  }

  // Check if the user has the required permission
  hasPermission(action: string | string[], module: string): boolean {
    const modulePermissions = this.getModulePermission(module) || [];
    return Array.isArray(action)
      ? action.some((requiredAction) => this.hasActionPermission(modulePermissions, requiredAction))
      : this.hasActionPermission(modulePermissions, action);
  }

  private resolveLoginTarget(loginRole = "admin") {
    return this.loginEndpointMap[loginRole] || this.loginEndpointMap.admin;
  }

  login(credentials: { phone: string; otp: string }, loginRole = "admin") {
    const loginTarget = this.resolveLoginTarget(loginRole);
    const url = environment.baseAPIHost + loginTarget.check;
    return this.http.post(url, { ...credentials, loginRole }, {withCredentials: true}).pipe(
      tap((response: any) => {

        if (response.success) {
          const userData = {
            ...(response.data.user || {}),
            eventOrganizer: response.data.eventOrganizer || undefined,
            temple: response.data.temple || undefined,
          };
          const access = response.data.access;
          this.accessToken = response.data.token;
          // this.refreshToken = response.refreshToken;
          this.localStorage.setItem(
            "currentUser",
            { ...userData, displayName: userData.name, photoURL: "" }
          );
          this.localStorage.setItem("accessToken", this.accessToken);
          this.localStorage.setItem('pageAccessData', access);
          this.localStorage.setItem("loginRole", loginRole);
          this.pageAccessData.next(access);
          // this.localStorage.setItem("refreshToken", this.refreshToken);
          this.isAuthenticated.next(true);
        }

      }),
      // catchError(this.handleError)
    );
  }

  verifyPhoneNumber(credentials: { phone: string }, loginRole = "admin") {
    const loginTarget = this.resolveLoginTarget(loginRole);
    const url = environment.baseAPIHost + loginTarget.verify;
    return this.http.post(url, { ...credentials, loginRole });
  }

  sendOTPAgain(credentials: { phone: string }) {
    const url = environment.baseAPIHost + environment.authVerifyPhoneResendOTP;
    return this.http.post(url, credentials);
  }

  // Store access token in memory (or localStorage if needed)
  setAccessToken(token: string): void {
    this.accessTokenSubject.next(token);
    this.localStorage.setItem("accessToken", token);
  }

  getAccessToken(): string | null {
    return this.accessTokenSubject.value || this.localStorage.getItem("accessToken");
  }

  getUserInfo(){
    if(this.currentUserLoginInfo.value){
      return this.currentUserLoginInfo.value;
    }else{
      const user = this.localStorage.getItem('currentUser');
      
      if(user){
        this.currentUserLoginInfo.next(user);
      }
      return user;
    }
  }

  refreshTokenRequest(): Observable<any> {
    if (this.isRefreshing) {
      // If already refreshing, wait for the token to be updated
      return this.refreshTokenSubject.pipe(
        filter((token) => token !== null),
        take(1)
      );
    }

    this.isRefreshing = true;
    this.refreshTokenSubject.next(null);
    const url = environment.baseAPIHost + environment.authRefreshToken;
    return this.http.post<any>(url, {}, { withCredentials: true }).pipe(
      tap((response) => {

        this.localStorage.setItem("accessToken", response.token);
        this.refreshTokenSubject.next(response.token);
      }),
      map((response) => response.token),
      finalize(() => {
        this.isRefreshing = false;
      })
    );
  }

  logout() {
    this.accessToken = null;
    this.refreshToken = null;
    this.localStorage.removeItem("accessToken");
    this.localStorage.removeItem("refreshToken");
    this.localStorage.removeItem('pageAccessData');
    this.localStorage.removeItem("currentUser");
    this.localStorage.removeItem("loginRole");
    this.isAuthenticated.next(false);
    this.currentUserLoginInfo.next(null);
    this.router.navigate(["/login"]);
  }

  getToken(): string | null {
    return this.accessToken || this.localStorage.getItem("accessToken");
  }

  isLoggedIn() {
    return this.isAuthenticated.asObservable();
  }

  private handleError(error: HttpErrorResponse) {
    return throwError(() => error.error.message || "Server Error");
  }

  isUserAuthenticated(): boolean {
    const token = this.localStorage.getItem('accessToken');
    const currentUser = this.localStorage.getItem('currentUser');
    const pageAccessData = this.localStorage.getItem('pageAccessData');
    if (token && currentUser && pageAccessData) {
      this.isAuthenticated.next(true);
      this.pageAccessData.next(pageAccessData);
    }
    return Boolean(token) && Boolean(currentUser) && Boolean(pageAccessData);  // Returns true if a token exists
  }
}


// refreshTokenRequest() {
//   const url = environment.baseAPIHost + environment.authRefreshToken ;
//   return this.http
//     .post(url, { refreshToken: this.getToken() })
//     .pipe(
//       tap((response: any) => {
//         this.accessToken = response.token;
//         this.localStorage.setItem('accessToken', this.accessToken);
//       }),
//       catchError(this.handleError)
//     );
// }
